import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { PaymentData } from '@/types/api';

const KEY = ['payments'] as const;

export type PaymentMethod = 'check' | 'cash' | 'ach' | 'card' | 'stripe' | 'other';

export type PaymentAllocationInput = {
  invoice_id: number;
  amount_cents: number;
};

export type PaymentInput = {
  received_on: string;
  method: PaymentMethod;
  reference?: string | null;
  amount_cents: number;
  bank_transaction_id?: number | null;
  notes?: string | null;
  allocations: PaymentAllocationInput[];
};

export function useClientPayments(clientId: number | undefined) {
  return useQuery({
    queryKey: clientId == null ? [...KEY, 'client', null] : [...KEY, 'client', clientId],
    enabled: clientId != null,
    queryFn: async (): Promise<PaymentData[]> => {
      const { data } = await api.get<PaymentData[]>(`/v1/clients/${clientId}/payments`);
      return data;
    },
  });
}

export function useCreatePayment(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PaymentInput): Promise<PaymentData> => {
      await primeCsrf();
      const { data } = await api.post<PaymentData>(`/v1/clients/${clientId}/payments`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useDeletePayment(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paymentId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/payments/${paymentId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
